/**
 * MtTooltip — 轻量 tooltip，完全不侵入子元素。
 *
 * - `display: contents` span 做容器，不产生布局盒模型，不影响 ButtonGroup
 * - 原生 DOM addEventListener 挂到实际子元素，不覆盖 HeroUI 的 React props/ref
 * - 气泡渲染在 portal，基于子元素 DOM rect 定位
 * - 桌面端 hover（150ms 延迟关闭）、移动端长按触发
 */
import { type ReactNode, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

type Placement = 'top' | 'bottom' | 'left' | 'right';

export interface MtTooltipProps {
  content: ReactNode;
  description?: ReactNode;
  placement?: Placement;
  children: ReactNode;
  /** 移动端自定义气泡内容，替代默认 content+description。传入 close 回调可在选择后关闭。 */
  mobileContent?: (close: () => void) => ReactNode;
}

const OPEN_DELAY = 500;
const CLOSE_DELAY = 150;
const WARM_WINDOW = 300;
const LONG_PRESS_MS = 500;
const GAP = 8;
const isTouch = () => 'ontouchstart' in window || navigator.maxTouchPoints > 0;

// 全局互斥：同一时刻只显示一个 tooltip，用实例引用做身份判定
let activeEntry: { close: () => void } | null = null;
let lastCloseTime = 0;

function calcPos(tr: DOMRect, tt: DOMRect, p: Placement) {
  let top = 0,
    left = 0;
  if (p === 'top') {
    top = tr.top - tt.height - GAP;
    left = tr.left + (tr.width - tt.width) / 2;
  } else if (p === 'bottom') {
    top = tr.bottom + GAP;
    left = tr.left + (tr.width - tt.width) / 2;
  } else if (p === 'left') {
    top = tr.top + (tr.height - tt.height) / 2;
    left = tr.left - tt.width - GAP;
  } else {
    top = tr.top + (tr.height - tt.height) / 2;
    left = tr.right + GAP;
  }
  const pad = 6;
  left = Math.max(pad, Math.min(left, window.innerWidth - tt.width - pad));
  top = Math.max(pad, Math.min(top, window.innerHeight - tt.height - pad));
  return { top, left };
}

export function MtTooltip({
  content,
  description,
  placement = 'top',
  children,
  mobileContent,
}: MtTooltipProps) {
  const wrapperRef = useRef<HTMLSpanElement>(null);
  const triggerEl = useRef<HTMLElement | null>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const close = useCallback(() => setOpen(false), []);
  const tapStart = useRef(0);
  const mobileContentRef = useRef(mobileContent);
  mobileContentRef.current = mobileContent;

  const openTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lpTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const touchDev = useRef(false);
  // 当前实例在全局互斥链中的标识
  const entryRef = useRef<{ close: () => void } | null>(null);

  useEffect(() => {
    touchDev.current = isTouch();
  }, []);

  const cancelTimers = useCallback(() => {
    if (openTimer.current) {
      clearTimeout(openTimer.current);
      openTimer.current = null;
    }
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  }, []);
  const doOpen = useCallback(() => {
    // 互斥：关闭上一个 tooltip
    if (activeEntry) activeEntry.close();
    setOpen(true);
    const entry = {
      close: () => {
        setOpen(false);
        lastCloseTime = Date.now();
      },
    };
    entryRef.current = entry;
    activeEntry = entry;
  }, []);
  const scheduleClose = useCallback(() => {
    cancelTimers();
    closeTimer.current = setTimeout(() => {
      setOpen(false);
      lastCloseTime = Date.now();
      // 仅当自己仍是活跃实例时才清除全局引用
      if (activeEntry === entryRef.current) activeEntry = null;
      closeTimer.current = null;
    }, CLOSE_DELAY);
  }, [cancelTimers]);
  const clearLp = useCallback(() => {
    if (lpTimer.current) {
      clearTimeout(lpTimer.current);
      lpTimer.current = null;
    }
  }, []);

  const handleEnter = useCallback(() => {
    if (touchDev.current) return;
    cancelTimers();
    // 刚关闭过另一个 tooltip → 跳过延迟直接显示（warm-up）
    if (Date.now() - lastCloseTime < WARM_WINDOW) {
      doOpen();
    } else {
      openTimer.current = setTimeout(() => {
        doOpen();
        openTimer.current = null;
      }, OPEN_DELAY);
    }
  }, [cancelTimers, doOpen]);
  const handleLeave = useCallback(() => {
    if (!touchDev.current) {
      cancelTimers();
      scheduleClose();
    }
  }, [cancelTimers, scheduleClose]);
  const handleDown = useCallback(() => {
    if (!touchDev.current) return;
    tapStart.current = Date.now();
    clearLp();
    // 有 mobileContent 时走 tap 触发，否则走长按
    if (!mobileContentRef.current) {
      lpTimer.current = setTimeout(() => {
        setOpen(true);
        lpTimer.current = null;
      }, LONG_PRESS_MS);
    }
  }, [clearLp]);
  const handleUp = useCallback(() => {
    clearLp();
    // 移动端 + mobileContent：tap 切换气泡
    if (touchDev.current && mobileContentRef.current && Date.now() - tapStart.current < 300) {
      setOpen((prev) => !prev);
    }
  }, [clearLp]);

  // 用 ref 持有最新回调，避免 effect 随回调引用变化而重新订阅
  const handlersRef = useRef({ handleEnter, handleLeave, handleDown, handleUp });
  handlersRef.current = { handleEnter, handleLeave, handleDown, handleUp };

  // 原生 DOM 事件挂载到子元素，不干扰 HeroUI Button 内部 React 事件系统
  useEffect(() => {
    const el = wrapperRef.current?.firstElementChild as HTMLElement | null;
    if (!el) return;
    triggerEl.current = el;
    const enter = () => handlersRef.current.handleEnter();
    const leave = () => handlersRef.current.handleLeave();
    const down = () => handlersRef.current.handleDown();
    const up = () => handlersRef.current.handleUp();
    el.addEventListener('mouseenter', enter);
    el.addEventListener('mouseleave', leave);
    el.addEventListener('pointerdown', down, true);
    el.addEventListener('pointerup', up, true);
    return () => {
      el.removeEventListener('mouseenter', enter);
      el.removeEventListener('mouseleave', leave);
      el.removeEventListener('pointerdown', down, true);
      el.removeEventListener('pointerup', up, true);
    };
  }, []);

  useEffect(
    () => () => {
      cancelTimers();
      clearLp();
      // 卸载时仅清除自身持有的全局引用
      if (activeEntry === entryRef.current) activeEntry = null;
    },
    [cancelTimers, clearLp],
  );

  // 点击外部关闭气泡（移动端 + 桌面兜底）
  useEffect(() => {
    if (!open) return;
    const handler = (e: PointerEvent) => {
      const tt = tooltipRef.current;
      const tr = triggerEl.current;
      const target = e.target as Node;
      if (tt?.contains(target) || tr?.contains(target)) return;
      setOpen(false);
    };
    const id = setTimeout(() => document.addEventListener('pointerdown', handler, true), 50);
    return () => {
      clearTimeout(id);
      document.removeEventListener('pointerdown', handler, true);
    };
  }, [open]);

  // 定位：paint 前同步计算
  useLayoutEffect(() => {
    if (!open) {
      setPos(null);
      return;
    }
    const tr = triggerEl.current?.getBoundingClientRect();
    const tt = tooltipRef.current?.getBoundingClientRect();
    if (tr && tt) setPos(calcPos(tr, tt, placement));
  }, [open, placement]);

  // 打开期间跟随滚动/resize 重新定位
  useEffect(() => {
    if (!open) return;
    const reposition = () => {
      const tr = triggerEl.current?.getBoundingClientRect();
      const tt = tooltipRef.current?.getBoundingClientRect();
      if (tr && tt) setPos(calcPos(tr, tt, placement));
    };
    window.addEventListener('scroll', reposition, true);
    window.addEventListener('resize', reposition);
    return () => {
      window.removeEventListener('scroll', reposition, true);
      window.removeEventListener('resize', reposition);
    };
  }, [open, placement]);

  return (
    <>
      {/* display:contents 不产生盒模型，不影响 ButtonGroup 布局和 CSS 选择器 */}
      <span ref={wrapperRef} style={{ display: 'contents' }}>
        {children}
      </span>
      {open &&
        createPortal(
          <div
            ref={tooltipRef}
            role="tooltip"
            onMouseEnter={cancelTimers}
            onMouseLeave={scheduleClose}
            className="pointer-events-auto"
            style={{
              position: 'fixed',
              zIndex: 9999,
              top: pos?.top ?? -9999,
              left: pos?.left ?? -9999,
              visibility: pos ? 'visible' : 'hidden',
            }}
          >
            {/* 箭头：10px 宽的旋转方块，圆角 4px */}
            <span
              style={{
                position: 'absolute',
                width: 10,
                height: 10,
                background: 'white',
                borderRadius: 4,
                transform: 'rotate(45deg)',
                boxShadow: '1px 1px 3px rgba(0,0,0,0.06)',
                ...(placement === 'top'
                  ? { bottom: -4, left: '50%', marginLeft: -5 }
                  : placement === 'bottom'
                    ? { top: -4, left: '50%', marginLeft: -5 }
                    : placement === 'left'
                      ? { right: -4, top: '50%', marginTop: -5 }
                      : { left: -4, top: '50%', marginTop: -5 }),
              }}
            />
            {touchDev.current && mobileContent ? (
              <div
                className="relative z-10 rounded-xl bg-white p-2 text-sm shadow-md"
                style={{ color: '#18181B' }}
              >
                {mobileContent(close)}
              </div>
            ) : (
              <div
                className="relative z-10 flex items-center gap-2 rounded-full bg-white px-3 py-1.5 text-sm whitespace-nowrap shadow-md"
                style={{ color: '#18181B' }}
              >
                <span>{content}</span>
                {!touchDev.current && description && (
                  <span
                    className="rounded px-1.5 py-0.5 text-xs"
                    style={{ background: '#EBEBEC', color: '#71717A' }}
                  >
                    {description}
                  </span>
                )}
              </div>
            )}
          </div>,
          document.body,
        )}
    </>
  );
}
